/**
 * Where a visitor came from, and the Meta pixel.
 *
 * Three jobs, all best-effort — none of them may ever block the form:
 *
 *  1. **Attribution.** The ad's UTM tags and click ids are on the landing URL
 *     only; by the time the form is sent the visitor may have moved through
 *     three steps. So they are captured once, on arrival, into sessionStorage
 *     (first touch wins within a visit) and sent with the lead.
 *  2. **Visitor id.** A random id kept in localStorage, sent to Meta hashed as
 *     `external_id` so a Contact and a Lead from the same phone match up.
 *  3. **Meta pixel.** Loaded from our code, not Meta's pasted snippet, with its
 *     id fetched at runtime from `/studio/api/config`. The pixel is on these
 *     pages ONLY — never the console or any other app.
 */

export interface Attribution {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  fbclid?: string;
  gclid?: string;
  referrer?: string;
  landingUrl?: string;
}

const ATTR_KEY = 'studio:attr';
const VID_KEY = 'studio:vid';
const FBC_KEY = 'studio:fbc';

function safeGet(store: 'local' | 'session', key: string): string | null {
  try {
    return (store === 'local' ? localStorage : sessionStorage).getItem(key);
  } catch {
    return null;
  }
}

function safeSet(store: 'local' | 'session', key: string, value: string): void {
  try {
    (store === 'local' ? localStorage : sessionStorage).setItem(key, value);
  } catch {
    // Private mode / blocked storage: attribution is lost, the lead is not.
  }
}

function clip(v: string | null, max: number): string | undefined {
  const s = v?.trim();
  return s ? s.slice(0, max) : undefined;
}

/** Read the landing URL once per visit; later calls return the first capture. */
export function captureAttribution(): Attribution {
  const saved = safeGet('session', ATTR_KEY);
  if (saved) {
    try {
      return JSON.parse(saved) as Attribution;
    } catch {
      // fall through and recapture
    }
  }
  const q = new URLSearchParams(location.search);
  let referrer: string | undefined;
  try {
    // Only an outside referrer means anything; our own pages are noise.
    if (document.referrer && new URL(document.referrer).host !== location.host) {
      referrer = clip(document.referrer, 500);
    }
  } catch {
    referrer = undefined;
  }
  const attr: Attribution = {
    utmSource: clip(q.get('utm_source'), 200),
    utmMedium: clip(q.get('utm_medium'), 200),
    utmCampaign: clip(q.get('utm_campaign'), 200),
    utmContent: clip(q.get('utm_content'), 200),
    utmTerm: clip(q.get('utm_term'), 200),
    fbclid: clip(q.get('fbclid'), 500),
    gclid: clip(q.get('gclid'), 500),
    referrer,
    landingUrl: clip(location.href, 1000),
  };
  safeSet('session', ATTR_KEY, JSON.stringify(attr));
  return attr;
}

/** True when the visit arrived with any UTM tag — the form then skips "How did you find us?". */
export function hasUtm(a: Attribution): boolean {
  return Boolean(a.utmSource || a.utmMedium || a.utmCampaign);
}

export function newId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  }
}

export function visitorId(): string {
  const existing = safeGet('local', VID_KEY);
  if (existing) return existing;
  const id = newId();
  safeSet('local', VID_KEY, id);
  return id;
}

function cookie(name: string): string | undefined {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return m ? decodeURIComponent(m[1]!) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The click id in Meta's `_fbc` format. The pixel writes that cookie itself;
 * when the pixel is blocked we build the same value from `fbclid`, once, so
 * the server event can still be tied to the ad click.
 */
function fbc(attr: Attribution): string | undefined {
  const fromCookie = cookie('_fbc');
  if (fromCookie) return fromCookie;
  if (!attr.fbclid) return undefined;
  const saved = safeGet('session', FBC_KEY);
  if (saved) return saved;
  const built = `fb.1.${Date.now()}.${attr.fbclid}`;
  safeSet('session', FBC_KEY, built);
  return built;
}

/** What the server needs to send its copy of a pixel event. */
export function metaIds(attr: Attribution) {
  return { fbc: fbc(attr), fbp: cookie('_fbp'), externalId: visitorId() };
}

/**
 * Is there a Codasc session on this browser?
 *
 * The page is built for strangers, so it shows no account chrome by default —
 * but a signed-in customer who lands here from the console needs the way back.
 * Asking the platform is the only way to know: the session cookie is
 * HttpOnly, so the page cannot read it. Never throws, never blocks the form;
 * a failure just means the links stay hidden.
 */
export async function hasSession(): Promise<boolean> {
  try {
    const res = await fetch('/api/me', { credentials: 'same-origin' });
    return res.ok;
  } catch {
    return false;
  }
}

// ─── Meta pixel ──────────────────────────────────────────────────────────

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: unknown;
  loaded: boolean;
  version: string;
};

interface FbWindow extends Window {
  fbq?: Fbq;
  _fbq?: Fbq;
}

let pixelReady = false;

/**
 * Fetch the pixel id and start the pixel. Resolves either way; a missing id
 * (local dev) or a blocked script simply leaves `pixelEvent` a no-op.
 */
export async function initPixel(): Promise<void> {
  try {
    const res = await fetch('/studio/api/config');
    if (!res.ok) return;
    const { metaPixelId } = (await res.json()) as { metaPixelId: string | null };
    if (!metaPixelId || !/^\d{5,20}$/.test(metaPixelId)) return;

    const w = window as FbWindow;
    if (!w.fbq) {
      // Meta's loader stub, written out: queue calls until fbevents.js arrives.
      const n = function (...args: unknown[]) {
        if (n.callMethod) n.callMethod(...args);
        else n.queue.push(args);
      } as Fbq;
      n.queue = [];
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      w.fbq = n;
      w._fbq ??= n;
      const s = document.createElement('script');
      s.async = true;
      s.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(s);
    }
    w.fbq('init', metaPixelId, { external_id: visitorId() });
    w.fbq('track', 'PageView');
    pixelReady = true;
  } catch {
    // Never let the pixel take the form down with it.
  }
}

/** Fire a standard pixel event with the id the server copy will share. */
export function pixelEvent(
  name: 'Lead' | 'Contact',
  eventId: string,
  params: Record<string, unknown> = {},
): void {
  if (!pixelReady) return;
  try {
    (window as FbWindow).fbq?.('track', name, params, { eventID: eventId });
  } catch {
    // no-op
  }
}
